import { AiHistoryMessage, AiHistoryPart } from './ai-assistant.models';
import { AiMessageVm, AiPartVm, AiTextPartVm, AiThinkingPartVm, AiToolCallPartVm, AiToolResultPartVm } from './ai-assistant.view-models';

let partIdCounter = 0;

const nextId = (): string => {
  partIdCounter++;
  return `history-${partIdCounter}`;
};

const mapHistoryPart = (part: AiHistoryPart): AiPartVm | null => {
  if (part.type === 'OUTPUT') {
    const vm: AiTextPartVm = {
      id: nextId(),
      kind: 'output',
      complete: true,
      text: part.content,
    };
    return vm;
  }
  if (part.type === 'THINKING') {
    const vm: AiThinkingPartVm = {
      id: nextId(),
      kind: 'thinking',
      complete: true,
      text: part.content,
      summary: part.summary,
    };
    return vm;
  }
  if (part.type === 'TOOL_CALL') {
    const vm: AiToolCallPartVm = {
      id: nextId(),
      kind: 'tool-call',
      complete: true,
      toolCallId: part.toolCallId,
      toolName: part.toolName,
      toolArguments: part.toolArguments,
      pendingConsentId: part.consentRequired && !part.consentDecision ? (part.toolCallId ?? '') : undefined,
      consentDecision: part.consentDecision,
    };
    return vm;
  }
  if (part.type === 'TOOL_RESULT') {
    const vm: AiToolResultPartVm = {
      id: nextId(),
      kind: 'tool-result',
      complete: true,
      toolCallId: part.toolCallId,
      toolName: part.toolName,
      content: part.content,
      isError: part.isError,
    };
    return vm;
  }
  return null;
};

const getUserText = (message: AiHistoryMessage): string =>
  message.parts
    .filter((p) => p.type === 'OUTPUT')
    .map((p) => p.content)
    .join('');

export const mapHistoryToMessageVms = (messages: AiHistoryMessage[]): AiMessageVm[] => {
  const result: AiMessageVm[] = [];

  messages
    .filter((m, index) => !(index === 0 && m.role === 'system'))
    .forEach((message) => {
      if (message.role === 'user') {
        const text = getUserText(message);
        if (!text) return;
        result.push({
          id: nextId(),
          role: 'user',
          parts: [{ id: nextId(), kind: 'output', complete: true, text }],
          timestamp: message.timestamp,
        });
        return;
      }

      // assistant / tool roles → accumulate into an assistant bubble
      // Thread consecutive assistant messages into one bubble
      const lastMsg = result[result.length - 1];
      const parts = message.parts.map(mapHistoryPart).filter((p): p is AiPartVm => p !== null);

      if (parts.length === 0) return;

      if (lastMsg && lastMsg.role === 'assistant') {
        result[result.length - 1] = { ...lastMsg, parts: [...lastMsg.parts, ...parts] };
      } else {
        result.push({ id: nextId(), role: 'assistant', parts, timestamp: message.timestamp });
      }
    });

  return result;
};
